import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { products } from '../data-models/company-permission';
import { PlatformUser } from '../data-models/platform-user';
import { UserRoles } from '../user-roles';
import { EssentialObject, EssentialObjectService } from './essential-object.service';
import { GeneralService } from './general.service';
import { LocalService } from './local.service';
import { PlatformUserService } from './platform-user.service';

/**
 * Route-level product permission check.
 *
 * Subclass it and declare `product` to gate a route. The same check is currently
 * inlined in ~28 components' ngOnInit; those can migrate here one at a time.
 *
 * Resolution order:
 *  1. Permissions already on the essential object (the common case — no network).
 *  2. Deep link or hard refresh, where the essential object is still empty:
 *     rebuild the user from the stored UserKey, then fall back to a permissions
 *     fetch only if the user carries none.
 *
 * On success it forwards to GeneralService.resolvePermissionAlert, which shows the
 * "no permission" notice once if the user arrived here by being redirected.
 */
export abstract class ProductPermissionGuard implements CanActivate {

  protected abstract get product(): products;

  constructor(
    protected platformUserService: PlatformUserService,
    protected essentialObjectService: EssentialObjectService,
    protected generalService: GeneralService,
    protected localService: LocalService,
    protected router: Router
  ) { }

  canActivate(): Observable<boolean | UrlTree> {
    return this.essentialObjectService.essentialObject.pipe(
      switchMap((object: EssentialObject) => {
        if (object?.user && object?.selectedEntity?.key)
          return this.checkPermissions(object.user, object.selectedEntity.key, object.language);

        const userKey = this.localService.getJsonValue('UserKey');
        const entityKey = this.storedEntityKey();

        if (!userKey || !entityKey)
          return of(this.router.parseUrl('/login'));

        return this.platformUserService.fetchPlatformUser(userKey).pipe(
          switchMap((user: PlatformUser) => user
            ? this.checkPermissions(user, entityKey, this.localService.getJsonValue('language'))
            : of(this.router.parseUrl('/login')))
        );
      })
    );
  }

  private checkPermissions(user: PlatformUser, entityKey: string, language: string): Observable<boolean | UrlTree> {
    const isSuperAdmin = user.role == UserRoles.SuperAdmin;

    if (isSuperAdmin || user.CompanyPermissions?.length > 0)
      return of(this.validate(user.CompanyPermissions, isSuperAdmin, entityKey, language));

    return this.platformUserService.fetchUserPermissions(user).pipe(
      map((response: any) => {
        if (response?.success != true)
          return this.router.parseUrl('/login');

        // Data is the role string for super admins, otherwise the permission list.
        const superAdminResponse = response.Data == UserRoles.SuperAdmin;
        return this.validate(superAdminResponse ? [] : response.Data, superAdminResponse, entityKey, language);
      })
    );
  }

  private validate(permissions: any[], isSuperAdmin: boolean, entityKey: string, language: string): boolean | UrlTree {
    if (this.generalService.hasPermission(entityKey, this.product, permissions, isSuperAdmin)) {
      this.generalService.resolvePermissionAlert(language);
      return true;
    }

    return this.router.parseUrl(this.generalService.resolveRoute(entityKey, permissions));
  }

  /** The header stores the selected entity as `{ entityKey, userKey, tenantKey }`. */
  private storedEntityKey(): string {
    try {
      const stored = JSON.parse(this.localService.getJsonValue('selectedEntity'));
      return stored?.entityKey || stored?.key;
    } catch {
      return null;
    }
  }
}
